import React, { useState, useEffect, useCallback } from 'react';
import { newsAPI } from '../../../services/api';
import './AdminSections.css';

function NewsManagement() {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingNews, setEditingNews] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'announcement',
    author: '',
    isPinned: false
  });

  // Fetch all news (including drafts if you add that feature)
  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAllNews({ limit: 100 });
      setNewsList(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    try {
      if (editingNews) {
        // Update existing news
        await newsAPI.updateNews(editingNews.id, formData);
        setSuccessMessage('News updated successfully!');
      } else {
        // Create new news
        await newsAPI.createNews(formData);
        setSuccessMessage('News created successfully!');
      }

      // Reset form
      setFormData({
        title: '',
        content: '',
        category: 'announcement',
        author: '',
        isPinned: false
      });
      setEditingNews(null);

      // Refresh the list
      fetchNews();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving news:', err);
      setError(err.response?.data?.error || 'Failed to save news. Please try again.');
    }
  };

  // Handle edit button click
  const handleEdit = (news) => {
    setEditingNews(news);
    setFormData({
      title: news.title,
      content: news.content,
      category: news.category,
      author: news.author,
      isPinned: news.isPinned || false
    });
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingNews(null);
    setFormData({
      title: '',
      content: '',
      category: 'announcement',
      author: '',
      isPinned: false
    });
  };

  // Handle delete
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await newsAPI.deleteNews(id);
      setSuccessMessage('News deleted successfully!');
      fetchNews();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting news:', err);
      setError('Failed to delete news. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>📰 News Management</h2>
        <p className="section-subtitle">Create, edit, and manage news updates</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="success-message">
          ✅ {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* News Form */}
      <div className="admin-form-container">
        <h3>{editingNews ? '✏️ Edit News Update' : '➕ Create News Update'}</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              maxLength={255}
              placeholder="Enter news title..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="announcement">Announcement</option>
              <option value="patch">Patch</option>
              <option value="update">Update</option>
              <option value="feature">New Feature</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="author">Author *</label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              required
              maxLength={100}
              placeholder="e.g., Development Team, Game Design Team..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows={10}
              placeholder="Enter news content... Use double line breaks for paragraphs."
            />
            <small className="form-hint">
              Tip: Use bullet points with • symbol. Use two line breaks between paragraphs.
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isPinned"
                checked={formData.isPinned}
                onChange={handleInputChange}
              />
              <span>📌 Pin this news (appears at the top)</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editingNews ? '💾 Update News' : '➕ Create News'}
            </button>
            {editingNews && (
              <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* News List */}
      <div className="admin-data-list">
        <h3>📋 All News Updates</h3>

        {loading ? (
          <p>Loading news...</p>
        ) : newsList.length === 0 ? (
          <p className="empty-state">No news updates yet. Create one above!</p>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Date</th>
                  <th>Pinned</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newsList.map(news => (
                  <tr key={news.id} className={news.isPinned ? 'pinned-row' : ''}>
                    <td className="title-cell">
                      {news.isPinned && <span className="pin-badge">📌</span>}
                      {news.title}
                    </td>
                    <td>
                      <span className={`category-badge badge-${news.category}`}>
                        {news.category}
                      </span>
                    </td>
                    <td>{news.author}</td>
                    <td>{formatDate(news.date || news.publishDate)}</td>
                    <td>{news.isPinned ? '✅' : '❌'}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleEdit(news)}
                        className="btn-edit"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(news.id, news.title)}
                        className="btn-delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default NewsManagement;
